import { z } from 'zod'

import type { ApiClient } from '@/shared/api/client'
import { parseCursorPage } from '@/shared/api/paging'

import { attendanceModes, createEnrollmentPath, enrollmentsApi, enrollmentStatuses, movesOf, type AttendanceMode, type EnrollmentStatus, type StatusAction } from '../enrollments/enrollment'

/** Enrollments from Class Details: one class's enrollments. */
export const classEnrollmentsRoute = '/entitlements/classes/:classId/enrollments'

export function classEnrollmentsPath(classId: string): string {
  return `/entitlements/classes/${encodeURIComponent(classId)}/enrollments`
}

/** Create Enrollment with the class already chosen, as Class Details links to it. */
export function createClassEnrollmentPath(classId: string): string {
  return `${createEnrollmentPath}?${new URLSearchParams({ classId }).toString()}`
}

/** Bulk enrollment: many people, one by one or from user groups, in many classes. */
export const bulkEnrollmentPath = '/entitlements/enrollments/bulk'

/** The API's list of a class's enrollments. */
export function classEnrollmentsApi(classId: string): string {
  return `/entitlements/v1/classes/${encodeURIComponent(classId)}/enrollments`
}

/** The API's batch of rows added to and changed in a class, each saved on its own. */
export function classEnrollmentsBatchApi(classId: string): string {
  return `${classEnrollmentsApi(classId)}/batch`
}

export const enrollmentsBulkApi = `${enrollmentsApi}/bulk`

/** The most rows one batch takes; the roster saves no more at once. */
export const maxBatchRows = 500

/** The most classes, and people times classes, one bulk enrollment takes. */
export const maxBulkClasses = 50
export const maxBulkRows = 500

const classEnrollmentSchema = z.object({
  id: z.string(),
  userId: z.string(),
  // The list names each learner; a batch's answer, which a repeat may give after an erasure, names no one.
  userName: z.string().nullish(),
  status: z.enum(enrollmentStatuses),
  classroomId: z.string().nullish(),
  attendance: z.enum(attendanceModes),
  lateCancel: z.boolean(),
  retake: z.boolean(),
  externalId: z.string().nullish(),
  createdOn: z.string(),
  version: z.number().int(),
})

/** One of a class's enrollments, with the version a change to it sends back. */
export type ClassEnrollment = z.infer<typeof classEnrollmentSchema>

export function parseClassEnrollment(body: unknown): ClassEnrollment {
  return classEnrollmentSchema.parse(body)
}

/** Every one of the class's enrollments the viewer may read, page after page. */
export async function readClassEnrollments(client: ApiClient, classId: string, signal?: AbortSignal): Promise<readonly ClassEnrollment[]> {
  const rows: ClassEnrollment[] = []
  let cursor: string | null = null
  do {
    const query: URLSearchParams = new URLSearchParams({ limit: '100', ...(cursor === null ? {} : { cursor }) })
    // oxlint-disable-next-line eslint/no-await-in-loop -- each page names the next
    const page: { readonly data: readonly ClassEnrollment[]; readonly pagination: { readonly nextCursor: string | null } } = parseCursorPage(await client.get(`${classEnrollmentsApi(classId)}?${query.toString()}`, signal), parseClassEnrollment)
    rows.push(...page.data)
    cursor = page.pagination.nextCursor
  } while (cursor !== null)
  return rows
}

const problemSchema = z.object({
  code: z.string(),
  message: z.string(),
  errors: z.record(z.string(), z.array(z.string())).nullish(),
})

/** Why a row was not saved: the rule's code, and for clashes each kind with what it clashes with. */
export type RowProblem = z.infer<typeof problemSchema>

export const rowOutcomes = ['saved', 'waitlisted', 'refused'] as const

const batchSchema = z.object({
  rows: z.array(
    z.object({
      outcome: z.enum(rowOutcomes),
      enrollment: classEnrollmentSchema.nullish(),
      problem: problemSchema.nullish(),
    }),
  ),
  savedCount: z.number().int(),
})

/** What a batch did: a result per row, in the order sent. */
export type BatchAnswer = z.infer<typeof batchSchema>

export function parseBatchAnswer(body: unknown): BatchAnswer {
  return batchSchema.parse(body)
}

const bulkSchema = z.object({
  rows: z.array(
    z.object({
      userId: z.string(),
      classId: z.string(),
      outcome: z.enum(rowOutcomes),
      enrollmentId: z.string().nullish(),
      problem: problemSchema.nullish(),
    }),
  ),
  savedCount: z.number().int(),
  waitlistedCount: z.number().int(),
  skippedCount: z.number().int(),
})

/** What a bulk enrollment did: a line per person and class. */
export type BulkAnswer = z.infer<typeof bulkSchema>

export function parseBulkAnswer(body: unknown): BulkAnswer {
  return bulkSchema.parse(body)
}

/** The API's code for a row that clashes with other classes, which may be sent again confirmed. */
export const conflictsCode = 'enrollment.conflicts'

// The status each move ends in.
const targetOf: Readonly<Record<StatusAction, EnrollmentStatus>> = {
  approve: 'enrolled',
  deny: 'denied',
  waitlist: 'waitlisted',
  enroll: 'enrolled',
  cancel: 'canceled',
  reinstate: 'requested',
}

/** The statuses an enrollment may be moved to from its own, with the move each takes, in the menu's order. */
export function statusTargets(status: EnrollmentStatus): readonly { readonly status: EnrollmentStatus; readonly action: StatusAction }[] {
  const seen = new Set<EnrollmentStatus>([status])
  return movesOf(status).flatMap((action) => {
    const target = targetOf[action]
    if (seen.has(target)) {
      return []
    }
    seen.add(target)
    return [{ status: target, action }]
  })
}

/**
 * Where a person sits, as the roster's Room column chooses it: one of the class's rooms, or no
 * room, attending virtually, in person or to be decided.
 */
export type Place = { readonly kind: 'room'; readonly classroomId: string } | { readonly kind: 'none'; readonly attendance: AttendanceMode }

export function placeOf(enrollment: Pick<ClassEnrollment, 'classroomId' | 'attendance'>): Place {
  return enrollment.classroomId === null || enrollment.classroomId === undefined || enrollment.classroomId === ''
    ? { kind: 'none', attendance: enrollment.attendance }
    : { kind: 'room', classroomId: enrollment.classroomId }
}

/** A place as one select value. */
export function placeKey(place: Place): string {
  return place.kind === 'room' ? `room:${place.classroomId}` : place.attendance
}

export function placeFromKey(key: string): Place {
  if (key.startsWith('room:')) {
    return { kind: 'room', classroomId: key.slice('room:'.length) }
  }
  const attendance = attendanceModes.find((mode) => mode === key) ?? 'toBeDecided'
  return { kind: 'none', attendance }
}

/** A place as a batch row's fields: a room is attended in person. */
export function placeFields(place: Place): { readonly classroomId: string | null; readonly attendance: AttendanceMode } {
  return place.kind === 'room' ? { classroomId: place.classroomId, attendance: 'inPerson' } : { classroomId: null, attendance: place.attendance }
}
